import {
  addDays,
  diffDays,
  formatAxisDate,
  getFirstOfMonth,
  getMonday,
  maxDate,
  minDate,
  toStartOfUtcDay,
} from './gantt-date-utils';
import {
  AXIS_HEIGHT,
  BAR_HEIGHT,
  CHART_VERTICAL_PADDING,
  LABEL_COLUMN_WIDTH,
  MAX_PX_PER_DAY,
  MILESTONE_SIZE,
  MIN_PX_PER_DAY,
  PX_PER_DAY_BY_ZOOM,
  PX_PER_DAY_DAY_THRESHOLD,
  PX_PER_DAY_WEEK_THRESHOLD,
  ROW_HEIGHT,
  SECTION_ROW_HEIGHT,
  TIMELINE_LEFT_PADDING,
  TIMELINE_RIGHT_PADDING,
} from './gantt-constants';
import {
  GanttTaskStatus,
  GanttZoomLevel,
  type GanttLayout,
  type GanttGridLine,
  type GanttRowLayout,
  type GanttTask,
  type GanttTaskLayout,
  type GanttTaskRowLayout,
  type ParsedGantt,
  type GanttSectionRowLayout,
} from './gantt-types';

interface BuildLayoutInput {
  parsed: ParsedGantt;
  pxPerDay: number;
  collapsedSections: Set<number>;
  timelineViewportWidth?: number;
}

const GANTT_SECTION_ROW_KIND: GanttSectionRowLayout['kind'] = 'section';
const GANTT_TASK_ROW_KIND: GanttTaskRowLayout['kind'] = 'task';

// Pick which axis-label / grid-line granularity to use given a px-per-day value.
// Below 3.5 px/day a daily tick is unreadable, so collapse to monthly; below 14
// daily ticks crowd, so use weekly.
export function getZoomLevelForPxPerDay(pxPerDay: number): GanttZoomLevel {
  if (pxPerDay >= PX_PER_DAY_DAY_THRESHOLD) {
    return GanttZoomLevel.Day;
  }

  if (pxPerDay >= PX_PER_DAY_WEEK_THRESHOLD) {
    return GanttZoomLevel.Week;
  }

  return GanttZoomLevel.Month;
}

// Compute the px-per-day that fits the entire date range into the timeline
// viewport. Falls back to a range-based default when the viewport hasn't been
// measured yet (initial render before ResizeObserver fires).
export function getFitPxPerDay(rangeDays: number, viewportWidth: number): number {
  const normalizedRangeDays = Math.max(1, rangeDays);

  if (viewportWidth <= 0) {
    if (normalizedRangeDays < 30) {
      return PX_PER_DAY_BY_ZOOM[GanttZoomLevel.Day];
    }

    if (normalizedRangeDays <= 120) {
      return PX_PER_DAY_BY_ZOOM[GanttZoomLevel.Week];
    }

    return PX_PER_DAY_BY_ZOOM[GanttZoomLevel.Month];
  }

  const availableWidth = Math.max(1, viewportWidth - TIMELINE_LEFT_PADDING - TIMELINE_RIGHT_PADDING);
  const fittedPxPerDay = availableWidth / normalizedRangeDays;
  return clampPxPerDay(fittedPxPerDay);
}

export function clampPxPerDay(pxPerDay: number): number {
  if (!Number.isFinite(pxPerDay) || pxPerDay <= 0) {
    return MIN_PX_PER_DAY;
  }

  return Math.max(MIN_PX_PER_DAY, Math.min(MAX_PX_PER_DAY, pxPerDay));
}

function getDateRange(tasks: GanttTask[]): { start: Date; end: Date } {
  const firstTask = tasks[0];
  const minStartDate = tasks.reduce((currentMin, task) => {
    return minDate(currentMin, task.startDate);
  }, firstTask.startDate);
  const maxEndDate = tasks.reduce((currentMax, task) => {
    return maxDate(currentMax, task.endDate);
  }, firstTask.endDate);

  return {
    start: addDays(minStartDate, -1),
    end: addDays(maxEndDate, 1),
  };
}

function addMonths(date: Date, months: number): Date {
  const normalized = toStartOfUtcDay(date);
  return new Date(Date.UTC(normalized.getUTCFullYear(), normalized.getUTCMonth() + months, 1));
}

function buildGridLines(input: {
  rangeStart: Date;
  rangeEnd: Date;
  zoomLevel: GanttZoomLevel;
  pxPerDay: number;
}): GanttGridLine[] {
  const { rangeStart, rangeEnd, zoomLevel, pxPerDay } = input;
  const rangeStartUtc = toStartOfUtcDay(rangeStart);
  const rangeEndUtc = toStartOfUtcDay(rangeEnd);

  const startIterator = zoomLevel === GanttZoomLevel.Day
    ? rangeStartUtc
    : zoomLevel === GanttZoomLevel.Week
      ? getMonday(rangeStartUtc)
      : getFirstOfMonth(rangeStartUtc);

  const lines: GanttGridLine[] = [];
  const pushGridLine = (date: Date) => {
    const x = TIMELINE_LEFT_PADDING + diffDays(rangeStartUtc, date) * pxPerDay;
    if (x < 0) {
      return;
    }

    const hasDuplicate = lines.some((line) => {
      return Math.abs(line.x - x) < 0.5;
    });
    if (hasDuplicate) {
      return;
    }

    lines.push({
      x,
      date,
      label: formatAxisDate(date, zoomLevel),
    });
  };

  let cursor = toStartOfUtcDay(startIterator);

  while (cursor.getTime() <= rangeEndUtc.getTime()) {
    pushGridLine(cursor);

    if (zoomLevel === GanttZoomLevel.Day) {
      cursor = addDays(cursor, 1);
    } else if (zoomLevel === GanttZoomLevel.Week) {
      cursor = addDays(cursor, 7);
    } else {
      cursor = addMonths(cursor, 1);
    }
  }

  // Always include the exact visible range boundaries so axis labels span the full width.
  pushGridLine(rangeStartUtc);
  pushGridLine(rangeEndUtc);

  lines.sort((a, b) => a.x - b.x);

  return lines;
}

function getTaskProgressRatio(task: GanttTask, today: Date): number {
  if (task.status !== GanttTaskStatus.Active) {
    return 0;
  }

  const start = toStartOfUtcDay(task.startDate);
  const end = toStartOfUtcDay(task.endDate);

  if (today.getTime() <= start.getTime()) {
    return 0;
  }

  if (today.getTime() >= end.getTime()) {
    return 1;
  }

  const totalDays = Math.max(1, diffDays(start, end));
  const elapsedDays = Math.max(0, diffDays(start, today));

  return Math.min(1, elapsedDays / totalDays);
}

export function buildGanttLayout(input: BuildLayoutInput): GanttLayout {
  const range = getDateRange(input.parsed.tasks);
  const totalDays = Math.max(1, diffDays(range.start, range.end));
  const viewportWidth = input.timelineViewportWidth ?? 0;
  const pxPerDay = clampPxPerDay(input.pxPerDay);
  const zoomLevel = getZoomLevelForPxPerDay(pxPerDay);
  const rightPadding = TIMELINE_RIGHT_PADDING;
  const contentWidth = TIMELINE_LEFT_PADDING + totalDays * pxPerDay + rightPadding;
  // When the chart fits the viewport, expand the timeline canvas to fill it so
  // gridlines and section bands stretch edge-to-edge instead of leaving a gap.
  const timelineWidth = Math.max(Math.max(420, viewportWidth), contentWidth);

  const rows: GanttRowLayout[] = [];
  const sectionRows: GanttSectionRowLayout[] = [];
  const taskRows: GanttTaskRowLayout[] = [];
  const taskLayouts: GanttTaskLayout[] = [];
  const taskLayoutById = new Map<string, GanttTaskLayout>();

  const today = toStartOfUtcDay(new Date());

  let yCursor = AXIS_HEIGHT + CHART_VERTICAL_PADDING;

  input.parsed.sections.forEach((section) => {
    const isCollapsed = input.collapsedSections.has(section.index);

    const sectionRow: GanttSectionRowLayout = {
      kind: GANTT_SECTION_ROW_KIND,
      sectionIndex: section.index,
      label: section.label,
      y: yCursor,
      height: SECTION_ROW_HEIGHT,
      isCollapsed,
    };

    rows.push(sectionRow);
    sectionRows.push(sectionRow);
    yCursor += SECTION_ROW_HEIGHT;

    if (isCollapsed) {
      return;
    }

    section.taskIds.forEach((taskId) => {
      const task = input.parsed.taskById.get(taskId);
      if (!task) {
        return;
      }

      const row: GanttTaskRowLayout = {
        kind: GANTT_TASK_ROW_KIND,
        taskId,
        sectionIndex: section.index,
        y: yCursor,
        height: ROW_HEIGHT,
      };

      rows.push(row);
      taskRows.push(row);

      const startX = TIMELINE_LEFT_PADDING + diffDays(range.start, task.startDate) * pxPerDay;
      const rawWidth = Math.max(pxPerDay * 0.6, diffDays(task.startDate, task.endDate) * pxPerDay);
      const width = task.status === GanttTaskStatus.Milestone
        ? MILESTONE_SIZE
        : Math.max(pxPerDay * 0.8, rawWidth);
      const barY = yCursor + (ROW_HEIGHT - BAR_HEIGHT) / 2;
      const progressRatio = getTaskProgressRatio(task, today);

      const taskLayout: GanttTaskLayout = {
        task,
        x: task.status === GanttTaskStatus.Milestone ? startX - MILESTONE_SIZE / 2 : startX,
        y: barY,
        width,
        height: BAR_HEIGHT,
        centerY: yCursor + ROW_HEIGHT / 2,
        progressRatio,
      };

      taskLayouts.push(taskLayout);
      taskLayoutById.set(task.id, taskLayout);
      yCursor += ROW_HEIGHT;
    });
  });

  const todayX = today.getTime() >= range.start.getTime() && today.getTime() <= range.end.getTime()
    ? TIMELINE_LEFT_PADDING + diffDays(range.start, today) * pxPerDay
    : null;

  const gridLines = buildGridLines({
    rangeStart: range.start,
    rangeEnd: range.end,
    zoomLevel,
    pxPerDay,
  });

  const chartHeight = yCursor + CHART_VERTICAL_PADDING;

  return {
    dateRange: range,
    totalDays,
    pxPerDay,
    timelineWidth,
    rows,
    sectionRows,
    taskRows,
    taskLayouts,
    taskLayoutById,
    gridLines,
    todayX,
    chartHeight,
    labelColumnWidth: LABEL_COLUMN_WIDTH,
    axisHeight: AXIS_HEIGHT,
    rowHeight: ROW_HEIGHT,
  };
}
